// Composable para conversión de archivos e imágenes
// Extrae funcionalidad de conversión de archivos de useBaseModule
// IMPORTANTE: Las dependencias se importan dinámicamente para evitar errores en navegador

/**
 * Composable para convertir formatos de archivo, especialmente imágenes HEIC
 */
export function useFileConverter() {
  /**
   * Convierte una imagen HEIC a JPEG usando heic2any
   * Compatible con navegadores modernos
   * @param file - Archivo HEIC a convertir
   * @param fileName - Nombre base para el archivo resultante (sin extensión)
   * @param callback - Función callback que recibe el archivo convertido
   */
  async function heicToJpg(
    file: File,
    fileName: string,
    callback?: (file: File) => void
  ): Promise<void> {
    try {
      // Importación dinámica de heic2any
      const heic2any = (await import("heic2any")).default;

      // Convertir HEIC a JPEG usando heic2any
      const convertedBlob = (await heic2any({
        blob: file,
        toType: "image/jpeg",
        quality: 0.9,
      })) as Blob;

      // Crear un nuevo File desde el Blob convertido
      const jpegFile = new File([convertedBlob], `${fileName}.jpg`, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });

      if (callback) {
        callback(jpegFile);
      }
    } catch (error) {
      console.error("Error en conversión HEIC:", error);
      throw error;
    }
  }

  /**
   * Convierte una URL de datos (data URL) a un objeto File
   * @param dataurl - URL de datos en formato base64
   * @param filename - Nombre para el archivo resultante
   * @returns Objeto File creado desde la data URL
   */
  function dataURLtoFile(dataurl: string, filename: string): File {
    const arr = dataurl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
    const bstr = atob(arr[arr.length - 1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  return {
    heicToJpg,
    dataURLtoFile,
  };
}
